// src/task-centre/TaskCentre.jsx
import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../useAuth";
import { canEditTasks, canViewTasks } from "../accessControl";
import TaskFilters from "./TaskFilters";
import TaskList from "./TaskList";
import TaskDetailsDrawer from "./TaskDetailsDrawer";
import {
  addActionAttachment,
  addActionNote,
  createAction,
  getAction,
  getActions,
  updateAction,
} from "../api/actions.api.js";
import { spacing, typography } from "../ui/tokens";
import {
  pageHeaderStyle,
  pageHeaderTitleBlockStyle,
  pageShellStyle,
  pageSubtitleStyle,
  pageTitleStyle,
  surfaceCardStyle,
} from "../ui/pagePatterns";

const contentStackStyle = {
  display: "grid",
  gap: spacing.xl,
  minHeight: 0,
};

const filtersPanelStyle = {
  ...surfaceCardStyle,
  padding: spacing.xl,
  background: "#f8fafc",
};

const listPanelStyle = {
  ...surfaceCardStyle,
  padding: spacing.xl,
  position: "relative",
  overflow: "hidden",
  minHeight: 0,
};

const STATUS_MAP = {
  OPEN: "Not started",
  IN_PROGRESS: "In progress",
  BLOCKED: "Needs review",
  COMPLETED: "Completed",
};

const STATUS_REVERSE_MAP = {
  "Not started": "OPEN",
  "In progress": "IN_PROGRESS",
  "Needs review": "BLOCKED",
  Completed: "COMPLETED",
};

const PRIORITY_MAP = {
  LOW: "Low",
  MEDIUM: "Medium",
  HIGH: "High",
  CRITICAL: "Critical",
};

const PRIORITY_REVERSE_MAP = {
  Low: "LOW",
  Medium: "MEDIUM",
  High: "HIGH",
  Critical: "CRITICAL",
};

const normalizeDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString();
};

const normalizeFramework = (category) => {
  if (!category) return "Custom";
  const normalized = String(category).trim();
  return normalized.length > 0 ? normalized : "Custom";
};

const mapHistoryEntry = (entry) => {
  if (!entry) return null;
  const field = entry.field || "Updated";
  const fromVal = entry.from_value || "";
  const toVal = entry.to_value || "";
  const detail = fromVal || toVal ? `${fromVal} -> ${toVal}` : entry.note || "";
  return {
    id: entry.id,
    action: entry.note || field,
    detail,
    at: entry.changed_at,
  };
};

const mapEvidenceEntry = (entry, type, fallbackDate) => {
  if (!entry) return null;
  if (type === "attachment") {
    return {
      id: `att-${entry.id}`,
      label: entry.description || entry.url || "Attachment",
      url: entry.url || "",
      note: "",
      addedAt: entry.uploaded_at || fallbackDate,
    };
  }
  if (type === "note") {
    return {
      id: `note-${entry.id}`,
      label: "Note",
      url: "",
      note: entry.body || "",
      addedAt: entry.created_at || fallbackDate,
    };
  }
  return null;
};

const mapActionToTask = (action) => {
  const fallbackDate = action?.updated_at || action?.created_at || new Date().toISOString();
  const attachments = Array.isArray(action?.attachments) ? action.attachments : [];
  const notes = Array.isArray(action?.notes) ? action.notes : [];
  const evidenceLinks = Array.isArray(action?.evidence_links) ? action.evidence_links : [];
  const historyEntries = Array.isArray(action?.history) ? action.history : [];

  return {
    id: action.id,
    title: action.title || action.name || "",
    description: action.description || "",
    controlId: action.source_ref || (action.control_id ? String(action.control_id) : ""),
    framework: normalizeFramework(action.category),
    status: STATUS_MAP[action.status] || "Not started",
    priority: PRIORITY_MAP[action.priority] || "Medium",
    dueDate: normalizeDate(action.due_date),
    owner: action.owner || action.assignee || "unassigned",
    evidence: [
      ...attachments.map((entry) => mapEvidenceEntry(entry, "attachment", fallbackDate)).filter(Boolean),
      ...notes.map((entry) => mapEvidenceEntry(entry, "note", fallbackDate)).filter(Boolean),
      ...evidenceLinks.map((link, index) => ({
        id: `link-${index}`,
        label: link,
        url: link,
        note: "",
        addedAt: fallbackDate,
      })),
    ],
    history: historyEntries.map(mapHistoryEntry).filter(Boolean),
  };
};

const mapTaskToActionPayload = (task) => ({
  title: task.title,
  description: task.description || null,
  category: normalizeFramework(task.framework),
  owner: task.owner || null,
  status: STATUS_REVERSE_MAP[task.status] || "OPEN",
  priority: PRIORITY_REVERSE_MAP[task.priority] || "MEDIUM",
  due_date: task.dueDate || null,
  source_ref: task.controlId || null,
});

function matchesFilters(task, filters = {}) {
  const { status, priority, framework, dueMode, search } = filters;

  if (status && status !== "All" && task.status !== status) return false;
  if (priority && priority !== "All" && task.priority !== priority) return false;
  if (framework && framework !== "All" && task.framework !== framework) return false;

  const today = new Date();
  const due = task.dueDate ? new Date(task.dueDate) : null;

  if (dueMode === "Overdue" && (!due || due >= today)) return false;

  if (dueMode === "ThisWeek") {
    const oneWeek = 7 * 24 * 60 * 60 * 1000;
    if (!due || due < today || due > new Date(today.getTime() + oneWeek)) return false;
  }

  if (search) {
    const s = search.toLowerCase();
    const combined = `${task.title} ${task.description} ${task.controlId} ${task.framework}`.toLowerCase();
    if (!combined.includes(s)) return false;
  }

  return true;
}

export default function TaskCentre() {
  const { user } = useAuth();
  const canEdit = canEditTasks(user);
  const canView = canViewTasks(user);
  const [allTasks, setAllTasks] = useState([]);
  const [visibleTasks, setVisibleTasks] = useState([]);
  const [filters, setFilters] = useState({
    status: "All",
    priority: "All",
    framework: "All",
    dueMode: "All",
    search: "",
  });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [selectedTask, setSelectedTask] = useState(null);

  useEffect(() => {
    const filtered = allTasks.filter((t) => matchesFilters(t, filters));
    setVisibleTasks(filtered);
  }, [allTasks, filters]);

  const loadActions = useCallback(async () => {
    if (!canView) { setAllTasks([]); setLoading(false); return; }
    try {
      setLoading(true);
      setLoadError("");
      const res = await getActions();
      const payload = res?.data ?? res;
      const items = Array.isArray(payload?.items) ? payload.items : Array.isArray(payload) ? payload : [];
      setAllTasks(items.map(mapActionToTask));
    } catch (err) {
      console.error("Failed to load tasks", err);
      setAllTasks([]);
      setLoadError("We could not load the current task queue. Refresh and try again.");
    } finally {
      setLoading(false);
    }
  }, [canView]);

  const loadActionDetail = useCallback(async (id) => {
    if (!id) return;
    try {
      const res = await getAction(id);
      const payload = res?.data ?? res;
      if (!payload) return;
      const mapped = mapActionToTask(payload);
      setSelectedTask(mapped);
      setAllTasks((prev) => prev.map((task) => (task.id === mapped.id ? { ...task, ...mapped } : task)));
    } catch (err) {
      console.error("Failed to load task detail", err);
    }
  }, []);

  useEffect(() => {
    loadActions();
  }, [loadActions]);

  const handleTaskClick = async (id) => {
    const task = allTasks.find((t) => t.id === id) || null;
    setSelectedTask(task);
    await loadActionDetail(id);
  };

  const handleSaveTask = useCallback(
    async (updatedTask) => {
      if (!canEdit) return;
      try {
        if (!updatedTask.id) {
          const res = await createAction(mapTaskToActionPayload(updatedTask));
          const created = res?.data ?? res;
          await loadActions();
          await loadActionDetail(created?.id);
        } else {
          await updateAction(updatedTask.id, mapTaskToActionPayload(updatedTask));
          await loadActions();
          await loadActionDetail(updatedTask.id);
        }
      } catch (err) {
        console.error("Failed to save task", err);
      }
    },
    [canEdit, loadActions, loadActionDetail]
  );

  const handleCreateTask = useCallback(() => {
    if (!canEdit) return;
    const today = new Date().toISOString().slice(0, 10);
    setSelectedTask({
      id: null,
      title: "",
      description: "",
      controlId: "",
      framework: "SOC2",
      status: "Not started",
      priority: "Medium",
      dueDate: today,
      owner: "unassigned",
      evidence: [],
      history: [],
    });
  }, [canEdit]);

  const handleCompleteTask = useCallback((taskId) => {
    if (!canEdit) return;
    const run = async () => {
      try {
        await updateAction(taskId, { status: "COMPLETED" });
        await loadActions();
        await loadActionDetail(taskId);
      } catch (err) {
        console.error("Failed to complete task", err);
      }
    };
    run();
  }, [canEdit, loadActions, loadActionDetail]);

  const handleAddEvidence = useCallback((taskId, evidence) => {
    if (!canEdit) return;
    const run = async () => {
      try {
        const owner = selectedTask?.owner || null;
        if (evidence.url) {
          await addActionAttachment(taskId, {
            url: evidence.url,
            description: evidence.label || evidence.note || null,
            uploaded_by: owner,
          });
        } else {
          const bodyParts = [evidence.label, evidence.note].filter(Boolean);
          const body = bodyParts.join(" - ");
          if (body) {
            await addActionNote(taskId, { body, author: owner });
          }
        }
        await loadActionDetail(taskId);
      } catch (err) {
        console.error("Failed to add evidence", err);
      }
    };
    run();
  }, [canEdit, loadActionDetail, selectedTask]);

  useEffect(() => {
    const handler = () => handleCreateTask();
    window.addEventListener("openCreateTask", handler);
    return () => window.removeEventListener("openCreateTask", handler);
  }, [handleCreateTask]);

  if (!canView) return <p>Task Centre requires View access to Tasks and Actions.</p>;

  return (
    <div style={{ ...pageShellStyle, height: "100%" }}>
      <div style={pageHeaderStyle}>
        <div style={pageHeaderTitleBlockStyle}>
          <h1 style={pageTitleStyle}>Task Centre</h1>
          <p style={pageSubtitleStyle}>Track task status, priorities, and due dates from one workflow view.</p>
        </div>
        <div style={{ fontSize: typography.size.sm, color: "#64748b" }}>
          {visibleTasks.length} visible task{visibleTasks.length === 1 ? "" : "s"}
        </div>
      </div>

      <div style={contentStackStyle}>
        <div style={filtersPanelStyle}>
          <TaskFilters filters={filters} onChange={setFilters} />
        </div>

        <div style={listPanelStyle}>
          <TaskList
            tasks={visibleTasks}
            loading={loading}
            error={loadError}
            onTaskClick={handleTaskClick}
            onCreateTask={canEdit ? handleCreateTask : undefined}
            onRetry={loadActions}
          />
        </div>
      </div>

      <TaskDetailsDrawer
        readOnly={!canEdit}
        task={selectedTask}
        onSave={handleSaveTask}
        onComplete={handleCompleteTask}
        onAddEvidence={handleAddEvidence}
        onClose={() => setSelectedTask(null)}
      />
    </div>
  );
}
